import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, map, Observable, of, shareReplay } from "rxjs";
import { StateAssociation } from "../models/state.association.model";
import { environment } from "../../environments/environment";
import { Agency } from "../models/agency.model";

@Injectable({
  providedIn: "root"
})
export class StateAssociationService {
  constructor(private http: HttpClient) {}

  // Cached observable that's shared across all subscribers
  private stateAssociationsCache$: Observable<StateAssociation[]> | null = null;

  /**
   * Gets all state associations with proper caching
   * Uses shareReplay to ensure only ONE HTTP request is made even with multiple subscribers
   */
  public getStateAssociations(): Observable<StateAssociation[]> {
    if (!this.stateAssociationsCache$) {
      this.stateAssociationsCache$ = this.http
        .get<
          StateAssociation[]
        >(`${environment.fulcrumUrl}/api/v1/state-associations`, this.buildHttpOptions())
        .pipe(
          catchError((error: HttpErrorResponse): Observable<StateAssociation[]> => {
            if (error.status === 404) {
              return of(new Array<StateAssociation>());
            }
            throw error;
          }),
          // shareReplay ensures the HTTP request is only made once
          // and the result is cached and shared with all subscribers
          shareReplay({ bufferSize: 1, refCount: false })
        );
    }
    return this.stateAssociationsCache$;
  }

  public getStateAssociation(id: number | undefined): Observable<StateAssociation> {
    return this.http
      .get<StateAssociation>(
        `${environment.fulcrumUrl}/api/v1/state-associations/${id}`,
        this.buildHttpOptions()
      )
      .pipe(
        catchError((error: HttpErrorResponse): Observable<StateAssociation> => {
          throw error;
        })
      );
  }

  /**
   * Clears the cache - call this after updates to force a refresh
   */
  public clearCache(): void {
    this.stateAssociationsCache$ = null;
  }

  public updateStateAssociation(stateAssociation: StateAssociation): Observable<StateAssociation> {
    return this.http
      .put<StateAssociation>(
        `${environment.fulcrumUrl}/api/v1/state-associations/update`,
        stateAssociation,
        this.buildHttpOptions()
      )
      .pipe(
        map((updated) => {
          // Clear cache so next fetch gets fresh data
          this.clearCache();
          return updated;
        }),
        catchError((error: HttpErrorResponse): Observable<StateAssociation> => {
          throw error;
        })
      );
  }

  public addAgencyToStateAssociation(
    stateAssociationId: number,
    agencyId: number
  ): Observable<Agency> {
    return this.http
      .put<Agency>(
        `${environment.fulcrumUrl}/api/v1/state-associations/add-agency/${stateAssociationId}/${agencyId}`,
        {},
        this.buildHttpOptions()
      )
      .pipe(
        map((agency) => {
          this.clearCache();
          return agency;
        }),
        catchError((error: HttpErrorResponse): Observable<Agency> => {
          throw error;
        })
      );
  }

  public removeAgencyFromStateAssociation(agencyId: number): Observable<Agency> {
    return this.http
      .put<Agency>(
        `${environment.fulcrumUrl}/api/v1/state-associations/remove-agency/${agencyId}`,
        this.buildHttpOptions()
      )
      .pipe(
        map((agency) => {
          this.clearCache();
          return agency;
        }),
        catchError((error: HttpErrorResponse): Observable<Agency> => {
          throw error;
        })
      );
  }

  public uploadStateAssociationImage(
    stateAssociationId: number,
    imageBase64: string
  ): Observable<void> {
    return this.http.post<void>(
      `${environment.fulcrumUrl}/api/v1/state-associations/${stateAssociationId}/image`,
      imageBase64,
      this.buildHttpOptions()
    );
  }

  getStateAssociationImage(stateAssociationId: number): Observable<Blob> {
    return this.http.get(
      `${environment.fulcrumUrl}/api/v1/state-associations/${stateAssociationId}/image`,
      {
        responseType: "blob",
        headers: new HttpHeaders({
          Accept: "image/jpeg,image/png"
        })
      }
    );
  }

  private buildHttpOptions() {
    return {
      headers: new HttpHeaders({
        "Content-Type": "application/json"
      }),
      params: {}
    };
  }

  public getStateAssociationById(id: number): Observable<StateAssociation | undefined> {
    return this.getStateAssociations().pipe(
      map((associations: StateAssociation[]) => associations.find((assoc) => assoc.id === id))
    );
  }

  public getStateAssociationByState(state: string): Observable<StateAssociation | undefined> {
    return this.getStateAssociations().pipe(
      map((associations: StateAssociation[]) => associations.find((assoc) => assoc.state === state))
    );
  }
}
